import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Package, Eye, ShoppingCart } from 'lucide-react';
import { useTikTokFetch, Product, formatCurrency, formatNumber } from '../hooks/useTikTokFetch';
import '../styles/pages.css';

export default function Products() {
  const { get, getShopId } = useTikTokFetch();
  const shopId = getShopId();
  const [page, setPage] = useState(0);
  const limit = 20;

  const { data: products = [], isLoading } = useQuery<Product[]>({
    queryKey: ['products', shopId, page],
    queryFn: () => get(`/api/shop/${shopId}/products?limit=${limit}&offset=${page * limit}`),
    enabled: !!shopId,
  });

  return (
    <div className="page-container">
      <h1 className="page-title">Products</h1>

      {isLoading ? (
        <div className="loading-spinner" />
      ) : products.length === 0 ? (
        <div className="empty-state">
          <Package size={48} className="text-muted" />
          <p>No products synced yet</p>
        </div>
      ) : (
        <motion.div className="product-grid" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          {products.map((p) => (
            <motion.div
              key={p.id}
              className="product-card"
              whileHover={{ y: -4 }}
              transition={{ duration: 0.15 }}
            >
              <div className="product-image">
                {p.main_image_url ? (
                  <img src={p.main_image_url} alt={p.title} />
                ) : (
                  <div className="product-image-placeholder">
                    <Package size={32} />
                  </div>
                )}
                <span className={`badge product-stock ${p.is_in_stock ? 'badge--success' : 'badge--danger'}`}>
                  {p.is_in_stock ? 'In Stock' : 'Out of Stock'}
                </span>
              </div>
              <div className="product-info">
                <h3 className="product-title">{p.title}</h3>
                <span className="product-price">
                  {p.price !== null ? formatCurrency(p.price, p.currency) : 'N/A'}
                </span>
                <div className="product-stats">
                  <span><Eye size={12} /> {formatNumber(p.total_views)}</span>
                  <span><ShoppingCart size={12} /> {formatNumber(p.total_sales)} sales</span>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      )}

      {products.length >= limit && (
        <div className="pagination">
          <button className="btn btn--sm" onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0}>
            Previous
          </button>
          <span className="text-secondary">Page {page + 1}</span>
          <button className="btn btn--sm" onClick={() => setPage(page + 1)}>Next</button>
        </div>
      )}
    </div>
  );
}
